import { useCookie } from 'nuxt/app'
import {
  FacebookClickIdCookie,
  FacebookBrowserIdCookie,
  EverflowClientCookie,
  EverflowClientSubIDCookie,
  EverflowClientAdditionalParametersCookie,
  EverflowClientSourceIDCookie,
  EverflowClientAffiliateIDCookie,
  McanismCookie,
  Media4MomentsCookie,
  AdtractionCookie,
  SnapchatClickIdCookie,
  UTMSourceCookie,
  UTMMediumCookie,
  UTMCampaignCookie,
  LandingPageUrlCookie,
  AttributionCookie
} from '@/enums/gtm'

/**
 * Every attribution group nulled, to use as default when updating attribution ids.
 * This is to override any existing, since we don't want combined data from multiple sources.
 */
export const nulledAttributionIds = {
  everflow: null,
  snapchat: null,
  facebook: null,
  tiktok: null,
  google: null,
  tracking: null,
  internal: null
} as const

export function useAttributionIds() {
  const everflowTransactionId = useCookie(EverflowClientCookie).value || undefined
  const everflowSubIds = useCookie(EverflowClientSubIDCookie).value || undefined
  const everflowSourceId = useCookie(EverflowClientSourceIDCookie).value || undefined
  const everflowAffiliateId = useCookie(EverflowClientAffiliateIDCookie).value || undefined
  const efAdditionalParams = useCookie(EverflowClientAdditionalParametersCookie).value || undefined
  const mcanismTrackId = useCookie(McanismCookie).value || undefined
  const m4mTid = useCookie(Media4MomentsCookie).value || undefined
  const adtrId = useCookie(AdtractionCookie).value || undefined
  const scCid = useCookie(SnapchatClickIdCookie).value || undefined
  const scBrowserId = useCookie('_scid').value || undefined
  const fbClickId = useCookie(FacebookClickIdCookie).value || undefined
  const fbBrowserId = useCookie(FacebookBrowserIdCookie).value || undefined
  const tiktokClickId = useCookie('ttclid').value || undefined
  const tiktokBrowserId = useCookie('_ttp').value || undefined
  const googleClickId = useCookie('ota-gads.gclid').value || undefined
  const gbraid = useCookie('ota-gads.gbraid').value || undefined
  const wbraid = useCookie('ota-gads.wbraid').value || undefined
  const utmSource = useCookie(UTMSourceCookie).value || undefined
  const utmMedium = useCookie(UTMMediumCookie).value || undefined
  const utmCampaign = useCookie(UTMCampaignCookie).value || undefined
  const landingPageUrl = useCookie(LandingPageUrlCookie).value || undefined
  const otaAttribution = useCookie(AttributionCookie).value || undefined
  const convertContextId = useCookie('ota-conv-context').value || undefined
  const cookieConsent = useCookie('CookieConsent').value || undefined

  const attributionIds = {
    ...(everflowTransactionId
      ? {
          everflow: {
            transactionId: everflowTransactionId,
            transaction_id: everflowTransactionId,
            ...(everflowSubIds ? { subIds: everflowSubIds, sub_ids: everflowSubIds } : {}),
            ...(everflowSourceId ? { sourceId: String(everflowSourceId), source_id: String(everflowSourceId) } : {}),
            ...(everflowAffiliateId ? { affId: everflowAffiliateId, aff_id: everflowAffiliateId } : {}),
            ...(efAdditionalParams ? { efAdditionalParams } : {}),
            ...(mcanismTrackId ? { mcanismTrackId } : {}),
            ...(m4mTid ? { m4mTid } : {}),
            ...(adtrId ? { adtrId } : {})
          }
        }
      : {}),
    ...(scCid
      ? {
          snapchat: {
            clickId: scCid,
            ...(scBrowserId ? { browserId: scBrowserId } : {}),
            ...(utmCampaign ? { campaign: utmCampaign } : {}),
            ...(utmMedium ? { medium: utmMedium } : {})
          }
        }
      : {}),
    ...(fbClickId
      ? {
          facebook: {
            clickId: fbClickId,
            browserId: fbBrowserId
          }
        }
      : {}),
    ...(tiktokClickId
      ? {
          tiktok: {
            clickId: tiktokClickId,
            ...(tiktokBrowserId ? { browserId: tiktokBrowserId } : {})
          }
        }
      : {}),
    ...(googleClickId
      ? {
          google: {
            clickId: googleClickId,
            ...(gbraid ? { gbraid } : {}),
            ...(wbraid ? { wbraid } : {})
          }
        }
      : {}),
    ...(utmSource || utmMedium || utmCampaign || landingPageUrl || otaAttribution
      ? {
          tracking: {
            ...(utmSource ? { utmSource } : {}),
            ...(utmMedium ? { utmMedium } : {}),
            ...(utmCampaign ? { utmCampaign } : {}),
            ...(landingPageUrl ? { landingPageUrl } : {}),
            ...(otaAttribution ? { otaAttribution } : {})
          }
        }
      : {}),
    ...(convertContextId || cookieConsent
      ? {
          internal: {
            ...(convertContextId ? { convertContextId } : {}),
            ...(cookieConsent ? { cookieConsent } : {})
          }
        }
      : {})
  }

  return { attributionIds }
}
